/**
 * TableDefinitionEditor Store
 *
 * Manages ephemeral UI state scoped to a single TableDefinitionEditor instance.
 * Provider is mounted inside `TableDefinitionEditor.organism` so each editor
 * instance gets its own store, replacing the editorType-keyed Flux pattern.
 */

import { type SetStoreState, createStore } from '@seeqdev/utils';
import type { ColumnDefinition, EvaluationDetailsCellContext } from '@/tableDefinitionEditor/tableDefinition.types';
import type { RulesResultV1 } from '@seeqdev/api/model/RulesResultV1';

export type EvaluationDetailsModalData = {
  evaluationColumn: ColumnDefinition;
  evaluationResults: RulesResultV1[];
  cellContext?: EvaluationDetailsCellContext;
};

export type TableDefinitionEditorState = {
  /**
   * When defined, the evaluation details modal is open and this describes what to show.
   * When `undefined`, the modal is closed.
   */
  evaluationDetailsModal: EvaluationDetailsModalData | undefined;
};

const initialState: TableDefinitionEditorState = {
  evaluationDetailsModal: undefined,
};

const tableDefinitionEditorActionsFactory = (set: SetStoreState<TableDefinitionEditorState>) => ({
  openEvaluationDetails: (data: EvaluationDetailsModalData) =>
    set((draft) => {
      draft.evaluationDetailsModal = data;
    }),

  closeEvaluationDetails: () =>
    set((draft) => {
      draft.evaluationDetailsModal = undefined;
    }),

  reset: () => set(() => initialState),
});

const [TableDefinitionEditorStoreProvider, useTableDefinitionEditorStoreInstance, useTableDefinitionEditorSelector] =
  createStore('TableDefinitionEditor', initialState, tableDefinitionEditorActionsFactory);

export { TableDefinitionEditorStoreProvider };

export const useEvaluationDetailsModal = () =>
  useTableDefinitionEditorSelector((state) => state.evaluationDetailsModal);

export const useTableDefinitionEditorActions = () => useTableDefinitionEditorStoreInstance().actions;
